import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, ExternalLink, Send, ShieldCheck } from "lucide-react";
import { Logo, Spinner } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { API, api, errText } from "@/lib/api";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

function NoteForm({ token, client, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/reports/shared/${token}/notes`, { text: text.trim() }); setText(""); onSent(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="tf-card p-5 sm:p-6" data-testid="shared-note-form">
      <p className="font-bold text-t-on text-lg">Leave a note for {client}</p>
      <p className="text-sm text-t-muted mt-1">Private — only {client} sees it, in their History. Up to 1,000 characters.</p>
      <textarea className="tf-input mt-3" maxLength={1000} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={`e.g. Thanks for sharing this, ${client}. Let's start with the criticism pattern on Thursday.`} data-testid="shared-note-input" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-t-muted">{text.length}/1000</span>
        <Btn size="md" icon={<Send size={16} />} label="Send note" loading={busy} disabled={!text.trim()} onClick={send} testId="shared-note-send" />
      </div>
      {err ? <p className="mt-2 text-sm" style={{ color: "var(--hot)" }} data-testid="shared-note-error">{err}</p> : null}
    </div>
  );
}

export default function SharedReport() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const load = () => api.get(`/reports/shared/${token}/info`).then(({ data }) => setInfo(data)).catch((e) => setError(errText(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = `${API}/reports/shared/${token}`;

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8">
        <div className="flex items-center gap-2.5"><Logo size={30} /><span className="font-bold text-t-on text-lg">Triggerific-Pilot</span></div>
        {error ? (
          <div className="tf-card p-10 text-center mt-10" data-testid="shared-error"><p className="font-semibold text-t-on">{error}</p></div>
        ) : !info ? <div className="mt-16 grid place-items-center"><Spinner className="text-t-brand" /></div> : (
          <div className="tf-rise">
            <p className="mt-10 tf-tag">Shared by {info.client}</p>
            <h1 className="mt-1 text-4xl sm:text-5xl font-bold text-t-on" data-testid="shared-title">{info.title}</h1>
            <p className="mt-2 text-t-on2">{info.period ? `${info.period} · ` : ""}Sent {day(info.sentAt)}</p>
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-t-muted"><ShieldCheck size={16} className="text-t-brand" /> Private link · available until {day(info.expiresAt)}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Btn size="md" icon={<ExternalLink size={16} />} label="Open report" onClick={() => window.open(pdf, "_blank", "noopener")} testId="shared-open-pdf" />
              <Btn size="md" variant="secondary" icon={<FileDown size={16} />} label="Download PDF" testId="shared-download-pdf"
                onClick={() => { const a = document.createElement("a"); a.href = pdf; a.download = `Triggerific-Pilot-${info.title.replace(/\s+/g, "-")}.pdf`; a.click(); }} />
            </div>
            <iframe title="Report" src={pdf} className="hidden sm:block mt-6 w-full h-[70vh] rounded-3xl border border-t-border bg-white" data-testid="shared-pdf-frame" />
            <div className="mt-6">
              {sent ? <div className="tf-card p-5 mb-4 bg-t-brand3" data-testid="shared-note-sent"><p className="font-semibold text-t-on">Thank you — your note has been shared with {info.client}.</p></div> : null}
              {info.notes.length ? (
                <div className="tf-card p-5 mb-4" data-testid="shared-notes-list">
                  <p className="tf-tag mb-2">Your notes on this report</p>
                  {info.notes.map((n, i) => <p key={i} className="py-2 border-t border-t-border first:border-0 text-t-on2 whitespace-pre-line"><span className="text-xs text-t-muted block">{new Date(n.at).toLocaleString()}</span>{n.text}{n.reply ? <span className="mt-2 block rounded-xl bg-t-brand3 px-3 py-2 text-sm text-t-on" data-testid={`shared-reply-${i}`}><span className="block text-xs text-t-muted">{info.client} replied · {new Date(n.reply.at).toLocaleDateString()}</span>{n.reply.text}</span> : null}</p>)}
                </div>
              ) : null}
              {info.notes.length >= 5
                ? <p className="text-sm text-t-muted" data-testid="shared-note-limit">You've left the maximum of 5 notes on this report. Thank you for supporting {info.client}.</p>
                : <NoteForm token={token} client={info.client} onSent={() => { setSent(true); load(); }} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
